import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { i18n } from "../boot/i18n";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

// Formats a min/max week range for display, e.g. "4 weeks" or "4-6 weeks".
// Returns an empty string when either bound is missing (unset).
function formatWeeks(minWeeks, maxWeeks) {
  if (
    minWeeks === null ||
    minWeeks === undefined ||
    maxWeeks === null ||
    maxWeeks === undefined
  ) {
    return "";
  }
  if (minWeeks === maxWeeks) {
    return i18n.global.tc("weeks", minWeeks, { count: minWeeks });
  }
  return i18n.global.t("weeksRange", { min: minWeeks, max: maxWeeks });
}

export const LEAD_TIME_FIELDS = {
  brand: "",
  minWeeks: null,
  maxWeeks: null,
  orderCutoff: null,
  seriesLeadTimes: {},
  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(LEAD_TIME_FIELDS);

export default class LeadTime extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, LEAD_TIME_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: LeadTime.canBeCreatedBy,
      edit: LeadTime.canBeEditedBy,
      delete: LeadTime.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeCreatedBy(user) {
    return user.isAdmin || user.hasRole("purchaser");
  }

  static canBeEditedBy(user) {
    return user.isAdmin || user.hasRole("purchaser");
  }

  static canBeDeletedBy(user) {
    return user.isAdmin || user.hasRole("purchaser");
  }

  // Database

  get path() {
    return LeadTime.getPath(this.id);
  }

  static getPath(id) {
    let path = "lead-times";
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(leadTime) {
    return leadTime.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new LeadTime(snapshot.id, data);
  }

  // Vendor/Brand lookup

  get vendor() {
    return store.state.main.companies[this.data.brand] || null;
  }

  get vendorName() {
    return this.vendor ? this.vendor.data.name : "";
  }

  get brandName() {
    return this.vendorName;
  }

  get hasSeriesLeadTimes() {
    return Object.keys(this.data.seriesLeadTimes).length > 0;
  }

  // Editing

  get rules() {
    // Lead times are filled in incrementally, so every value is optional.
    // When one half of a min/max pair is provided, the other becomes required
    // and max must be at least min.
    const isSet = (a) => a !== null && a !== undefined && a !== "";
    const nonNegative = (a) => !isSet(a) || a >= 0 || "valueNonNegative";
    const minRules = (max) => [
      nonNegative,
      (a) => !isSet(max) || isSet(a) || "required",
    ];
    const maxRules = (min) => [
      nonNegative,
      (a) => !isSet(a) || a >= (min || 0) || "maxWeeksMinimum",
      (a) => !isSet(min) || isSet(a) || "required",
    ];
    return {
      minWeeks: minRules(this.data.maxWeeks),
      maxWeeks: maxRules(this.data.minWeeks),
      seriesMinWeeks: (seriesData) => minRules(seriesData.maxWeeks),
      seriesMaxWeeks: (seriesData) => maxRules(seriesData.minWeeks),
    };
  }

  get isValid() {
    const { minWeeks, maxWeeks, seriesMinWeeks, seriesMaxWeeks } = this.rules;
    const defaultValid =
      minWeeks.every((rule) => rule(this.data.minWeeks) === true) &&
      maxWeeks.every((rule) => rule(this.data.maxWeeks) === true);
    if (!defaultValid) return false;
    return Object.values(this.data.seriesLeadTimes).every(
      (s) =>
        seriesMinWeeks(s).every((rule) => rule(s.minWeeks) === true) &&
        seriesMaxWeeks(s).every((rule) => rule(s.maxWeeks) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, LEAD_TIME_FIELDS)));
  }

  isEqual(leadTime) {
    return Boolean(
      this.id === leadTime.id && isEmpty(diff(this.data, leadTime.data))
    );
  }

  get modifiedDate() {
    return this.data.modified || this.data.created || null;
  }

  get isStale() {
    if (!this.modifiedDate) return true;
    const diffDays = Math.floor(
      (new Date() - this.modifiedDate) / (1000 * 60 * 60 * 24)
    );
    return diffDays > 14;
  }

  // Formatting

  get orderCutoffDisplay() {
    const cutoff = this.data.orderCutoff;
    if (!cutoff || !cutoff.day) {
      return "";
    }
    if (!cutoff.time) {
      return cutoff.day;
    }
    return `${cutoff.day} at ${cutoff.time}`;
  }

  get leadTimeDisplay() {
    return formatWeeks(this.data.minWeeks, this.data.maxWeeks);
  }

  getSeriesLeadTimeDisplay(series) {
    const seriesData = this.data.seriesLeadTimes[series];
    return formatWeeks(seriesData?.minWeeks, seriesData?.maxWeeks);
  }

  getLeadTimeForSeries(series) {
    if (this.hasSeriesLeadTimes && this.data.seriesLeadTimes[series]) {
      return this.data.seriesLeadTimes[series];
    }
    return {
      minWeeks: this.data.minWeeks,
      maxWeeks: this.data.maxWeeks,
    };
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }
}
